import styled from "@emotion/styled";
import UI from "@mile-meeting/mile-ui";
import { TMeetingRoom } from "apis/meetings/meeting-room";
import { useEffect, useRef } from "react";

const MeetingRoomInfoModal = ({
  meetingRoom,
  open,
  setOpen,
  position = { top: 104, left: 116 },
  canClose = true,
}: {
  meetingRoom: TMeetingRoom;
  open?: boolean;
  setOpen?: (value: boolean) => void;
  position?: { left: number; top: number };
  canClose?: boolean;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const closeModal = () => {
    const focusedElement = document.activeElement;
    if (focusedElement && focusedElement instanceof HTMLElement) {
      focusedElement.blur();
    }
    setOpen?.(false);
  };

  useEffect(() => {
    const handler = (event: any) => {
      if (open && ref.current && !ref.current.contains(event.target)) {
        closeModal();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [open]);

  return (
    <Container style={{ display: open ? "flex" : "none", top: position.top, left: position.left }} ref={ref}>
      {canClose && (
        <div style={{ position: "absolute", right: 12, top: 12, cursor: "pointer" }}>
          <UI.IconButton.Normal
            type="primary"
            icon="x"
            size={16}
            color="var(--alias-color-label-normal)"
            onClick={closeModal}
          />
        </div>
      )}
      <UI.Typography
        type="headline01"
        fontStyle="bold"
        color="var(--alias-color-label-normal)"
        style={{
          whiteSpace: "wrap",
        }}
      >
        {meetingRoom.name}
      </UI.Typography>
      {meetingRoom.image && meetingRoom.image !== "" ? (
        <img src={meetingRoom.image} width={160} height={160} style={{ objectFit: "cover", borderRadius: "4px" }} />
      ) : (
        <EmptyImage>
          <UI.Icon name="image" size={24} color="var(--alias-color-label-assistive)" />
        </EmptyImage>
      )}

      <InfoContainer>
        <LabelContainer>
          <UI.Icon name="office" size={16} color="var(--alias-color-label-assistive)" />
          <UI.Typography type="label01" fontStyle="medium" color="var(--alias-color-label-assistive)">
            위치
          </UI.Typography>
        </LabelContainer>
        <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
          {meetingRoom.building?.name ?? ""} {meetingRoom.floor?.name ?? ""}
        </UI.Typography>
      </InfoContainer>
      <InfoContainer>
        <LabelContainer>
          <UI.Icon name="people" size={16} color="var(--alias-color-label-assistive)" />
          <UI.Typography type="label01" fontStyle="medium" color="var(--alias-color-label-assistive)">
            수용 인원
          </UI.Typography>
        </LabelContainer>
        <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
          {meetingRoom.capacity}인
        </UI.Typography>
      </InfoContainer>
      <InfoContainer>
        <LabelContainer>
          <UI.Icon name="book" size={16} color="var(--alias-color-label-assistive)" />
          <UI.Typography type="label01" fontStyle="medium" color="var(--alias-color-label-assistive)">
            상세 설명
          </UI.Typography>
        </LabelContainer>
        <UI.Typography type="body01 normal" fontStyle="regular" color="var(--alias-color-label-normal)">
          {meetingRoom.description}
        </UI.Typography>
      </InfoContainer>
    </Container>
  );
};

export default MeetingRoomInfoModal;

const Container = styled.div`
  display: flex;
  padding: 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;

  position: absolute;
  z-index: 100;

  border-radius: 8px;
  background: white;

  box-shadow: var(--alias-style-shadow-strong);

  width: 200px;
  box-sizing: border-box;
`;

const EmptyImage = styled.div`
  display: flex;
  width: 160px;
  height: 160px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  border-radius: 4px;
  background: var(--component-color-fill-alternative);
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  align-self: stretch;
`;

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
